import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { CounselorNav } from '../../counselor-nav/counselor-nav';
import { Career } from '../../../../../models/career';
import { CareerPageService } from '../../../../../services/career-page.service';
import { JobListing } from '../../../../../models/job-listing';
import { ChangeDetectorRef } from '@angular/core';
import { PostService } from '../../../../../services/posts.service';
import { toFileUrl } from '../../../../../helpers/file-url';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSliderModule } from '@angular/material/slider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

/**
 * Create/edit form for a career listing. Handles both new career creation
 * and editing an existing one — mode is determined by the presence of an
 * `id` route param (anything other than 'new' is treated as an edit).
 */
@Component({
  selector: 'app-counselor-career-edit',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    CounselorNav,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDividerModule,
    MatChipsModule,
    MatCardModule,
    MatTooltipModule,
    MatSliderModule,
    MatAutocompleteModule,
  ],
  templateUrl: './counselor-careers-edit.html',
  styleUrl: './counselor-careers-edit.css',
})
export class CounselorCareerEdit implements OnInit {
  careerService = inject(CareerPageService);
  cdr = inject(ChangeDetectorRef);
  postService = inject(PostService);

  career: any;
  isEdit = false;

  /** Bound to the skill text input before it's added to `form.skills`. */
  skillInput = '';

  /** Bound to the related career text input before it's added to `form.relatedCareers`. */
  relatedInput = '';
  jobListings: JobListing[] = [];
  skills: any[] = [];
  categories: any[] = [];
  relatedCareers: Career[] = [];
  status = 'Draft';
  bannerPreview: string | null = null;
  bannerFile: File | null = null;
  bannerPositionY = 50;
  galleryPhotos: { id: string; file: File; preview: string; caption: string }[] = [];
  availablePosts: any[] = [];
  careers: Career[] = [];
  filteredCareers: any[] = [];
  linkedPosts: any[] = [];
  galleryFile!: File;

  emojiOptions = [
    { emoji: '💼', label: 'Business / General' },
    { emoji: '⚕️', label: 'Healthcare / Medicine' },
    { emoji: '⚖️', label: 'Law / Legal' },
    { emoji: '🏗️', label: 'Engineering / Construction' },
    { emoji: '💻', label: 'Technology / Software' },
    { emoji: '🎨', label: 'Art / Design / Creative' },
    { emoji: '📚', label: 'Education / Teaching' },
    { emoji: '🔬', label: 'Science / Research' },
    { emoji: '💰', label: 'Finance / Banking' },
    { emoji: '📈', label: 'Marketing / Sales' },
    { emoji: '🏛️', label: 'Government / Public Service' },
    { emoji: '✈️', label: 'Aviation / Travel' },
    { emoji: '🎭', label: 'Performing Arts / Entertainment' },
    { emoji: '🏋️', label: 'Fitness / Sports' },
    { emoji: '🍽️', label: 'Culinary / Food Service' },
    { emoji: '🌿', label: 'Environment / Agriculture' },
    { emoji: '🏠', label: 'Real Estate' },
    { emoji: '🚒', label: 'Emergency Services' },
    { emoji: '🧠', label: 'Psychology / Mental Health' },
    { emoji: '📰', label: 'Journalism / Media' },
    { emoji: '🎵', label: 'Music' },
    { emoji: '📦', label: 'Logistics / Supply Chain' },
    { emoji: '🔧', label: 'Trades / Skilled Labor' },
    { emoji: '🛡️', label: 'Military / Defense' },
    { emoji: '🌐', label: 'International / Diplomacy' },
  ];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    tagline: new FormControl('', Validators.required),
    career_type_id: new FormControl('', Validators.required),
    icon: new FormControl('💼', Validators.required),
    description: new FormControl('', Validators.required),
    salary: new FormControl(''),
    required_education: new FormControl(''),
    skills: new FormControl<any>([]),
    jobListings: new FormControl<JobListing[]>([]),
    relatedCareers: new FormControl<Career[]>([]),
    status: new FormControl('Draft'),
  });

  constructor(
    /** Provides access to the current route snapshot for reading the `id` param. */
    private route: ActivatedRoute,
    /** Router used to navigate back to the careers list after save. */
    private router: Router,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');

      if (id) {
        this.getCareer(id);
        this.isEdit = true;
      } else {
        this.loadPosts();
      }
    });
    this.getTypes();
    this.getCareers();
  }

  save(status: 'Draft' | 'Published') {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.syncJobListings();
    this.form.controls.status.setValue(status);
    const raw = this.form.getRawValue();
    const formData = new FormData();
    formData.append('name', raw.name ?? '');
    formData.append('tagline', raw.tagline ?? '');
    formData.append('career_type_id', raw.career_type_id ?? '');
    formData.append('icon', raw.icon ?? '💼');
    formData.append('description', raw.description ?? '');
    formData.append('salary', raw.salary ?? '');
    formData.append('required_education', raw.required_education ?? '');
    formData.append('status', status ?? '');
    formData.append('skills', JSON.stringify(this.skills ?? []));
    formData.append('jobListings', JSON.stringify(this.jobListings ?? []));

    if (this.bannerFile instanceof File) {
      formData.append('banner', this.bannerFile);
    }
    formData.append('bannerPositionY', String(this.bannerPositionY));

    const linkedPostIds = this.availablePosts.filter((p) => p.linked).map((p) => p.id);
    formData.append('linkedPosts', JSON.stringify(linkedPostIds));
    formData.append('relatedCareers', JSON.stringify(this.relatedCareers.map((c) => c.id)));

    // If the photos already exist in
    const existingPhotos = this.galleryPhotos
      .filter((p) => p.file === null && p.id)
      .map((p) => ({ id: p.id, caption: p.caption }));
    formData.append('existingGallery', JSON.stringify(existingPhotos));

    const galleryMeta = this.galleryPhotos
      .filter((p) => p.file !== null)
      .map(({ caption }, index) => ({
        caption,
        index,
      }));

    formData.append('galleryMeta', JSON.stringify(galleryMeta));

    this.galleryPhotos
      .filter((p) => p.file !== null)
      .forEach((photo, index) => {
        formData.append(`galleryFile_${index}`, photo.file);
      });

    if (this.isEdit) {
      this.careerService.updateCareer(this.career.id, formData).subscribe({
        next: (career) => {
          this.router.navigateByUrl('counselors/careers');
        },
      });
    } else {
      this.careerService.createCareer(formData).subscribe({
        next: (career) => {
          this.router.navigateByUrl('counselors/careers');
        },
      });
    }
  }

  getTypes() {
    this.careerService.getTypes().subscribe({
      next: (types) => {
        this.categories = types;
      },
    });
  }

  getCareer(id: string) {
    this.careerService.getCareerById(id).subscribe({
      next: (career) => {
        this.career = career;

        this.form.patchValue({
          name: career.name,
          tagline: career.tagline,
          career_type_id: String(career.career_type_id),
          icon: career.icon,
          description: career.description,
          salary: career.salary,
          required_education: career.required_education,
          status: career.status,
        });
        this.skills = career.skills || [];
        this.jobListings = career.JobListings || [];
        this.relatedCareers = career.RelatedCareerPages || [];
        this.linkedPosts = career.LinkedPosts || [];

        this.loadPosts();

        this.bannerPreview = toFileUrl(career.banner?.path);
        this.bannerPositionY = career.banner?.bannerPositionY || 50;
        this.galleryPhotos = career.CareerMedia.map((p) => ({
          id: p.id,
          file: null as any,
          preview: toFileUrl(p.path) ?? '',
          caption: p.caption,
        }));

        this.cdr.markForCheck();
      },
    });
  }

  getCareers() {
    this.careerService.getCareers().subscribe({
      next: (careers) => {
        this.careers = careers;
      },
    });
  }

  /**
   * Adds the current `skillInput` value to the skills list if non-empty and not a duplicate.
   * @returns {void}
   */
  addSkill(): void {
    const value = this.form.controls.skills.getRawValue();

    if (!value) return;

    this.skills = [...this.skills, { id: Date.now(), value }];
    this.skillInput = '';
    this.form.controls.skills.setValue('');
  }

  /**
   * Removes a skill from the skills list by value.
   * @param {string} skill - The skill string to remove.
   * @returns {void}
   */
  removeSkill(skill: any): void {
    this.skills = this.skills.filter((s) => s !== skill);
    this.form.controls.skills.setValue('');
  }

  // Job Listings

  /**
   * Appends a blank job listing entry to the jobListings array.
   * @returns {void}
   */
  addJob(): void {
    this.jobListings = [
      ...this.jobListings,
      {
        id: Date.now(),
        title: '',
        company: '',
        type: 'Full-time',
        location: '',
        salary: '',
        description: '',
        work_style: 'Remote',
      },
    ];
    this.syncJobListings();
  }

  removeJob(id: number) {
    this.jobListings = this.jobListings.filter((j) => j.id !== id);
    this.syncJobListings();
  }

  onBannerSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.bannerFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.bannerPreview = reader.result as string;
      this.cdr.markForCheck();
    };

    reader.readAsDataURL(file);
  }

  removeBanner() {
    this.bannerPreview = null;
    this.bannerFile = null;
    this.bannerPositionY = 50;
  }
  removeGalleryPhoto(id: string) {
    this.galleryPhotos = this.galleryPhotos.filter((p) => p.id !== id);
  }

  onGallerySelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    this.galleryFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.galleryPhotos.push({
        id: crypto.randomUUID(),
        file,
        preview: reader.result as string,
        caption: '',
      });

      this.cdr.markForCheck();
    };

    reader.readAsDataURL(file);
    (event.target as HTMLInputElement).value = '';
  }

  syncJobListings() {
    this.form.controls.jobListings.setValue([...this.jobListings]);
  }

  filterCareers(query: string) {
    const q = (String(query) ?? '').toLowerCase();
    this.filteredCareers = this.careers.filter(
      (c) => c.name.toLowerCase().includes(q) && !this.relatedCareers.find((r) => r.id === c.id),
    );
  }

  displayCareer(career: any): string {
    return '';
  }

  addRelated(career: any) {
    if (!career || this.relatedCareers.find((r) => r.id === career.id)) return;
    this.relatedCareers = [...this.relatedCareers, career];
    this.relatedInput = '';
    this.filteredCareers = this.careers.filter(
      (c) => !this.relatedCareers.find((r) => r.id === c.id),
    );
    this.form.controls.relatedCareers.setValue(this.relatedCareers);
  }

  removeRelated(id: number) {
    this.relatedCareers = this.relatedCareers.filter((c) => c.id !== id);
    this.filteredCareers = this.careers.filter(
      (c) => !this.relatedCareers.find((r) => r.id === c.id),
    );
    this.form.controls.relatedCareers.setValue(this.relatedCareers);
  }

  loadPosts() {
    this.postService.getPosts().subscribe((data) => {
      this.availablePosts = data.map((p) => ({
        ...p,
        linked: this.linkedPosts.some((lp) => lp.id === p.id),
      }));
      this.cdr.markForCheck();
    });
  }
  stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}
